import { ColDef } from 'ag-grid-community';
import { Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AgGridTable } from '@shared/components/ui/AgGridTable/AgGridTable';
import type { Warehouse } from '../../models/types';

interface WarehousesTableProps {
  warehouses: Warehouse[];
  onViewWarehouse?: (id: number) => void;
}

export function WarehousesTable({ warehouses, onViewWarehouse }: WarehousesTableProps) {
  const { t } = useTranslation('warehouseMapModule');

  const columnDefs: ColDef<Warehouse>[] = [
    {
      field: 'id',
      headerName: t('warehouses.columns.id'),
      sortable: true,
      minWidth: 80,
      width: 100,
    },
    {
      field: 'name',
      headerName: t('warehouses.columns.name'),
      sortable: true,
      minWidth: 200,
      cellRenderer: (params) => <span className="font-medium">{params.data.name}</span>,
    },
    {
      field: 'city',
      headerName: t('warehouses.columns.city'),
      sortable: true,
      minWidth: 180,
      cellRenderer: (params) => params.data.city || '-',
    },
    {
      field: 'timezone',
      headerName: t('warehouses.columns.timezone'),
      sortable: true,
      minWidth: 200,
      cellRenderer: (params) => <span className="font-mono text-xs">{params.data.timezone}</span>,
    },
    {
      field: 'created_at',
      headerName: t('warehouses.columns.createdAt'),
      sortable: true,
      minWidth: 160,
      cellRenderer: (params) => {
        if (!params.data.created_at) return '-';
        const formatted = new Date(params.data.created_at).toLocaleDateString();
        return <span className="text-sm">{formatted}</span>;
      },
    },
    {
      headerName: '',
      field: 'actions',
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      resizable: false,
      pinned: 'right',
      lockPosition: 'right',
      suppressMovable: true,
      suppressSizeToFit: true,
      suppressAutoSize: true,
      cellRenderer: (params) => (
        <div className="flex justify-end w-full">
          {onViewWarehouse && (
            <button
              onClick={() => onViewWarehouse(params.data.id)}
              className="inline-flex items-center justify-center rounded-md h-9 w-9 hover:bg-accent hover:text-accent-foreground transition-colors"
              title={t('warehouses.actions.view')}
            >
              <Eye className="h-4 w-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-4 flex items-center justify-end flex-wrap gap-3">
        <p className="text-sm text-muted-foreground">{t('warehouses.count_other', { count: warehouses.length })}</p>
      </div>

      <div className="mb-4 mt-4">
        <AgGridTable
          data={warehouses}
          columnDefs={columnDefs}
          height="calc(100vh - 250px)"
          className="rounded-lg shadow-sm"
          emptyMessage={t('warehouses.empty')}
        />
      </div>
    </div>
  );
}
